'use client';

import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { SharedSurveyProgressTimer } from '@/shared-survey';
import { Button, Tooltip } from '@/ui';

import ProjectChat from './project-response-chat';
import { isPollProject } from './project-response.helpers';
import ProjectUsabilityImageChoiceFormSync from './project-usability-image-choice-form-sync';
import { useUsabilitySidebarState } from './project-usability-recording.hooks';
import { useUsabilityResponseContext } from './project-usability-response-context';

export default function ProjectUsabilityResponseSidebar() {
  const t = useTranslations('Usability');

  const { project, session, stage, conversationOverText } =
    useUsabilityResponseContext();

  const {
    isOpen: isSidebarOpen,
    closeSidebar,
    openSidebar,
  } = useUsabilitySidebarState();

  const {
    isRecording,
    estimatedTotalTimeInSeconds,
    progress,
    handleSubmitWithSidebar,
    effectiveIsConversationOver,
    handleConversationOver,
    activityStore,
  } = session;
  const {
    sharedChatProps,
    reportProblemFlag,
    handleReportProblemSubmit,
    isImageChoiceActive,
    lastAssistantMessage,
  } = stage;

  return (
    <aside
      data-is-sidebar-open={isSidebarOpen}
      className='group relative flex flex-col rounded-tl-4xl border-white/32 bg-white transition-all duration-300 ease-in-out data-[is-sidebar-open=false]:w-20 data-[is-sidebar-open=false]:cursor-pointer data-[is-sidebar-open=false]:hover:bg-gray-50 data-[is-sidebar-open=true]:w-112.5'
      data-test='usability-sidebar'
    >
      {!isSidebarOpen && (
        // reuse-exception: empty full-bleed expand target; Button requires children and injects control styles
        <button
          type='button'
          onClick={openSidebar}
          aria-label={t('expandSidebarAria')}
          className='absolute inset-0 z-10 size-full cursor-pointer border-0 bg-transparent p-0'
        />
      )}
      <div className='flex w-full items-center group-data-[is-sidebar-open=false]:justify-center'>
        <div className='flex h-20 w-full items-center justify-between gap-2 px-6 py-4 group-data-[is-sidebar-open=false]:justify-center group-data-[is-sidebar-open=false]:px-0'>
          <div className='flex flex-1 items-center gap-2 group-data-[is-sidebar-open=false]:flex-col group-data-[is-sidebar-open=false]:gap-0'>
            <Tooltip
              content={
                isSidebarOpen ? t('hideSidebarAria') : t('showSidebarAria')
              }
            >
              <Button
                variant='ghost'
                size='sm'
                className='h-auto px-1 py-2'
                onClick={closeSidebar}
                aria-label={
                  isSidebarOpen ? t('hideSidebarAria') : t('showSidebarAria')
                }
                data-test='usability-sidebar-toggle'
              >
                <FontAwesomeIcon
                  icon={isSidebarOpen ? faChevronRight : faChevronLeft}
                  className='text-base text-gray-900'
                />
              </Button>
            </Tooltip>
            <div className='flex flex-1 flex-col gap-1 group-data-[is-sidebar-open=false]:hidden'>
              <div
                title={project.publicTitle ?? ''}
                className='w-full max-w-75 truncate text-base font-semibold text-nowrap'
              >
                {project.publicTitle}
              </div>
              {isRecording ? (
                <div
                  className='flex items-center gap-2'
                  data-test='usability-recording-indicator'
                >
                  <div className='size-3 rounded-full border border-red-600 p-px'>
                    <div className='size-full rounded-full bg-red-600' />
                  </div>
                  <span className='text-theysaid-dark text-[11px]/3'>
                    {t('voiceRecordingIndicator')}
                  </span>
                </div>
              ) : null}
            </div>
            {isRecording ? (
              <Tooltip content={t('voiceRecordingIndicator')}>
                <div
                  className='hidden items-center justify-center group-data-[is-sidebar-open=false]:flex'
                  data-test='usability-recording-indicator-collapsed'
                >
                  <div className='size-3 rounded-full border border-red-600 p-px'>
                    <div className='size-full rounded-full bg-red-600' />
                  </div>
                </div>
              </Tooltip>
            ) : null}
          </div>

          {!isPollProject(project.type, project.isPoll) && isSidebarOpen ? (
            <div className='flex items-center justify-center gap-2'>
              <SharedSurveyProgressTimer
                estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
                progress={progress}
              />
            </div>
          ) : null}
        </div>
      </div>
      <ProjectChat
        {...sharedChatProps}
        onSubmit={handleSubmitWithSidebar}
        isConversationOver={effectiveIsConversationOver}
        onConversationOver={handleConversationOver}
        conversationOverText={conversationOverText || t('finishAndClose')}
        activityStore={activityStore}
        onReportProblemSubmit={
          reportProblemFlag(handleReportProblemSubmit) ?? undefined
        }
        formProviderChildren={
          isImageChoiceActive && lastAssistantMessage ? (
            <ProjectUsabilityImageChoiceFormSync
              messageId={lastAssistantMessage.id}
            />
          ) : null
        }
      />
    </aside>
  );
}
